import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import {
  Box,
  CircularProgress,
  Collapse,
  Fade,
  Typography,
} from '@mui/material';
import { useMediaQuery, useTheme } from '@mui/material';
import { NeumorphicButton } from '../../components/glassmorphism/GlassmorphismStyles';

// MUI X TreeView imports

import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { HandymanAppState } from '../../configuration/redux/reducers';
import {
  getHandymanList,
  loadMoreHandymen,
  HANDYMAN_PAGE_SIZE,
} from './actions';
import {
  BrowseHandymanQuery,
  HandymanViewModel,
  CategoryViewModel,
  UserPortfolioItemViewModel,
  BrowseHandymanSortFields,
} from '../../configuration/api/apiSchema';
import { getAllCategories } from '../Categories/actions';
import { connect, ConnectedProps, useDispatch, useSelector } from 'react-redux';
import { flattenCategories, getAllCategoryKeys } from '../Categories/helper';
import debounce from 'lodash/debounce';
import HandymanCard from './components/HandymanCard';
import SearchOnMapPopup from './components/SearchOnMapPopup';
// import useDebounce from '../../hooks/UseDebounce';
import { ReactComponent as FiltersSvg } from '../../assets/icons/info/filters.svg';
import { ReactComponent as SortSvg } from '../../assets/icons/info/sort.svg';
import { getUserReviews } from '../Reviews/actions';
import BrowseHandymanFilters from './components/BrowseHandymanFilters';
import BrowseHandymanSort from './components/BrowseHandymanSort';

const mapDispatch = {
  getHandymanList,
  loadMoreHandymen,
  getUserReviews,
  getAllCategories,
};

const mapStateToProps = (state: HandymanAppState): StateFromProps => ({
  isLoading: state.handyman.isLoading,
  isLoadingMore: state.handyman.isLoadingMore,
  handymanList: state.handyman.handymanList,
  hasMore: state.handyman.hasMore,
  currentPage: state.handyman.currentPage,
  allCategories: state.category.allCategories,
  isFetchingCategories: state.category.isLoading,
  categoriesError: state.category.isError ? 'Failed to load categories' : null,
});

const connector = connect(mapStateToProps, mapDispatch);

type ReduxProps = ConnectedProps<typeof connector>;
type Props = ReduxProps & PassedProps;
/** Category data structure */
interface Category {
  key: string;
  name: string;
  subcategories: Category[];
}

/** Handyman model for typing */
interface Handyman {
  id: string;
  avatarUrl?: string;
  name: string;
  isVerified: boolean;
  registrationDate: string; // e.g. "24 March 2019"
  lastOnline: string; // e.g. "3 hours ago"
  rating: number;
  positiveFeedbackPercent: number; // e.g. 99
  reviewsCount: number;
  shortDescription: string;
  ratePerHour?: number;
  fixedPriceRate?: number;
  // This can include root or subcategory keys
  categories: string[];
  // NEW fields for city/district filters:
  city: string;
  cityDistrict?: string;
  userPortfolioItems: UserPortfolioItemViewModel[];
}

type SelectedHandyman = {
  id: string;
  name: string;
  categories: string[];
  city?: string;
  cityDistrict?: string;
};

// Categories will be loaded from backend
// Dynamic variables will be set in component state

// Transform CategoryViewModel to Category interface
const transformCategoryViewModel = (
  categories: CategoryViewModel[],
): Category[] => {
  return categories.map((cat) => ({
    key: cat.key || '',
    name: cat.name || '',
    subcategories: transformCategoryViewModel(cat.subcategories || []),
  }));
};

const mapViewModelToHandyman = (
  viewModelList: HandymanViewModel[],
  selectedCategoryKeys: Set<string>,
): Handyman[] => {
  const filtered =
    selectedCategoryKeys.size === 0
      ? viewModelList
      : viewModelList
          // leave handyman with choosen category
          .filter((vm) =>
            (vm.categories ?? []).some((c) =>
              selectedCategoryKeys.has(c.categoryKey ?? ''),
            ),
          )
          // leave categories only with choosen keys
          .map((vm) => ({
            ...vm,
            categories: (vm.categories ?? []).filter((c) =>
              selectedCategoryKeys.has(c.categoryKey ?? ''),
            ),
          }));

  return filtered.map((viewModel) => {
    const locationString =
      viewModel.serviceAreaAddress || viewModel.location || '';
    const locationParts = locationString.split(', ');

    const hourlyRates = (viewModel.categories ?? [])
      .map((cat) => cat.hourlyRate)
      .filter((v): v is number => v != null);

    const hourlyPrice = hourlyRates.length
      ? Math.min(...hourlyRates)
      : undefined;

    const fixedRates = (viewModel.categories ?? [])
      .map((cat) => cat.fixedPriceRate)
      .filter((v): v is number => v != null);

    const fixedPrice = fixedRates.length ? Math.min(...fixedRates) : undefined;

    return {
      id: viewModel.id || '1',
      avatarUrl: viewModel.avatarUrl || '',
      name: viewModel.fullName || 'Emily C.',
      isVerified: viewModel.isVerified || true,
      registrationDate: 'March 24, 2019', // Mock data
      lastOnline: '3 hours ago', // Mock data
      rating: viewModel.rating ?? 0,
      positiveFeedbackPercent: 99, // Mock data
      reviewsCount: viewModel.reviewsCount ?? 0,
      shortDescription:
        viewModel.bio ||
        'I am a thorough cleaner with a strong focus on detail and hygiene.',
      ratePerHour: hourlyPrice,
      fixedPriceRate: fixedPrice || 0,
      categories: viewModel.categories?.map(
        (category) => category.categoryKey || 'home_services',
      ) || ['home_services'],
      city:
        locationParts[locationParts.length - 2] ||
        locationParts[0] ||
        'Unknown', // Get city (second to last part)
      cityDistrict: locationParts[0] || '', // Get street/district (first part)
      userPortfolioItems: viewModel.userPortfolioItems ?? [],
      kycStatus: viewModel.isKysVerified,
      isStripeAccountVerified: viewModel.isStripeVerified,
      isEarlyBird: viewModel.isEarlyBird,
    };
  });
};

interface CardCategory {
  key: string;
  name: string;
  root: string;
  level: number;
}

function groupCategories(
  categories: Category[],
  root = '',
  level = 0,
): Record<string, CardCategory> {
  let map: Record<string, CardCategory> = {};
  for (const cat of categories) {
    map[cat.key] = {
      key: cat.key,
      name: cat.name,
      root: root || cat.key,
      level: level,
    };
    if (cat.subcategories.length === 0) {
      continue;
    }
    const subMap = groupCategories(
      cat.subcategories,
      root || cat.key,
      level + 1,
    );
    map = { ...map, ...subMap };
  }
  return map;
}

interface StateFromProps {
  isLoading: boolean;
  isLoadingMore: boolean;
  handymanList?: HandymanViewModel[];
  hasMore: boolean;
  currentPage: number;
  allCategories?: CategoryViewModel[];
  isFetchingCategories: boolean;
  categoriesError: string | null;
}

interface DispatchFromProps {
  getHandymanList: (query: BrowseHandymanQuery) => void;
  loadMoreHandymen: (query: BrowseHandymanQuery, page: number) => void;
  // getUserPortfolio: (userId?: string) => void;
  getUserReviews: (userId?: string) => void;
  getAllCategories: () => void;
}

export type BrowseHandymanFilterTypes =
  | 'categories'
  | 'location'
  | 'name'
  | 'price-range'
  | 'hourly-rate'
  | 'fixed-price-rate';

interface PassedProps {
  preselectedCategoryKey?: string;
  excludeFilters?: BrowseHandymanFilterTypes[];
  actionButtonTitle: string | ((handymanId: string) => string);
  onAction: (
    handyman?: SelectedHandyman,
    selectedCategoryKeys?: string[],
  ) => void;
  isNotHandyman?: boolean;
  address?: string | null;
  radiusKm?: number;
  needFilters?: boolean;
  sentProposalIds?: string[];
}

// type Props = StateFromProps & DispatchFromProps & PassedProps;

const BrowseHandymanList = (props: Props) => {
  const { needFilters = true } = props;
  const location = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [showLoadMoreFooter, setShowLoadMoreFooter] = useState(true);

  // Ref for intersection observer (lazy loading trigger)
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const dispatch = useDispatch();

  const userReviewsByUserId = useSelector(
    (s: HandymanAppState) => s.review.userReviewsByUserId,
  );

  const userReviewsLoadingByUserId = useSelector(
    (s: HandymanAppState) => s.review.userReviewsLoadingByUserId,
  );

  const ensureReviewsLoaded = useCallback(
    (handymanId: string) => {
      if (userReviewsByUserId?.[handymanId]) return;

      dispatch(getUserReviews(handymanId, 1, 10) as any); // page=1, pageSize=10
    },
    [dispatch, userReviewsByUserId],
  );

  // const ensurePortfolioLoaded = useCallback(
  //   (handymanId: string) => {
  //     if (userReviewsByUserId?.[handymanId]) return;

  //     props.getUserPortfolio(handymanId); // page=1, pageSize=10
  //   },
  //   [dispatch, userReviewsByUserId],
  // );
  // Track initial load to prevent skeleton flicker
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Transform backend categories to local format
  const transformedCategories = useMemo(() => {
    return props.allCategories
      ? transformCategoryViewModel(props.allCategories)
      : [];
  }, [props.allCategories]);
  // Flattened dictionary for easy "key -> name" lookup
  const categoryDictionary = useMemo(() => {
    return flattenCategories(transformedCategories);
  }, [transformedCategories]);

  // All possible category keys
  const ALL_CATEGORY_KEYS = useMemo(() => {
    return getAllCategoryKeys(transformedCategories);
  }, [transformedCategories]);

  const groupedCategories = groupCategories(transformedCategories);

  // Category selection: store a set of checked category keys
  const [selectedCategoryKeys, setSelectedCategoryKeys] = useState<Set<string>>(
    new Set(),
  );

  // We use string for search fields, numeric for sliders, etc.
  const [handymanSearchText, setHandymanSearchText] = useState<string>('');

  // Debounced search text to prevent excessive API calls
  // const debouncedSearchText = useDebounce(handymanSearchText, 300);ç
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 10000]);
  const [onlyVerified, setOnlyVerified] = useState<boolean>(false);
  const [hourlyRateRange, setHourlyRateRange] = useState<[number, number]>([
    0, 1000,
  ]);
  const [fixedPriceRange, setFixedPriceRange] = useState<[number, number]>([
    0, 10000,
  ]);

  // NEW: City + District states
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
  const [addressInputValue, setAddressInputValue] = useState<string>(''); // For the TextField input
  const [selectedCoordinates, setSelectedCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [radiusKm, setRadiusKm] = useState<number | null>(null);
  const [appliedRadiusKm, setAppliedRadiusKm] = useState<number | null>(null); // triggers fetch

  // API and pagination state
  type SortValue = BrowseHandymanSortFields;
  const [sort, setSort] = useState<SortValue>(BrowseHandymanSortFields.Default);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [isDesc, setIsDesc] = useState(true);

  // Map popup state
  const [isMapPopupOpen, setIsMapPopupOpen] = useState(false);

  // Mark initial load complete after first data fetch
  useEffect(() => {
    if (props.handymanList && props.handymanList.length > 0 && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [props.handymanList, isInitialLoad]);

  // Use ref to store the latest getHandymanList function to avoid recreating fetchHandymen
  const getHandymanListRef = useRef(props.getHandymanList);
  useEffect(() => {
    getHandymanListRef.current = props.getHandymanList;
  }, [props.getHandymanList]);

  // Build query object for API calls
  const buildQuery = useCallback(
    (radiusOverride?: number): BrowseHandymanQuery => {
      return {
        latitude: selectedCoordinates ? selectedCoordinates.lat : null,
        longitude: selectedCoordinates ? selectedCoordinates.lng : null,
        address: selectedAddress || null,
        radiusKm: radiusOverride ?? appliedRadiusKm ?? null,
        categoryKeys:
          selectedCategoryKeys.size > 0
            ? Array.from(selectedCategoryKeys)
            : null,
        searchTerm: searchTerm || null,
        minHourlyRate: hourlyRateRange[0] > 0 ? hourlyRateRange[0] : null,
        maxHourlyRate: hourlyRateRange[1] < 1000 ? hourlyRateRange[1] : null,
        minFixedPrice: fixedPriceRange[0] > 0 ? fixedPriceRange[0] : null,
        maxFixedPrice: fixedPriceRange[1] < 10000 ? fixedPriceRange[1] : null,
        verifiedOnly: onlyVerified || null,
        page: 1,
        pageSize: HANDYMAN_PAGE_SIZE,
        sort: sort || null,
        isDesc: isDesc,
      };
    },
    [
      selectedAddress,
      selectedCoordinates,
      appliedRadiusKm,
      selectedCategoryKeys,
      searchTerm,
      hourlyRateRange,
      fixedPriceRange,
      onlyVerified,
      sort,
      isDesc,
    ],
  );

  const fetchHandymen = useCallback(
    (radiusOverride?: number) => {
      const query = buildQuery(radiusOverride);
      getHandymanListRef.current(query);
    },
    [buildQuery],
  );

  // Load more handymen for lazy loading
  const handleLoadMore = useCallback(() => {
    if (props.isLoadingMore || !props.hasMore) return;

    const query = buildQuery();
    props.loadMoreHandymen(query, props.currentPage + 1);
  }, [
    buildQuery,
    props.isLoadingMore,
    props.hasMore,
    props.currentPage,
    props.loadMoreHandymen,
  ]);

  const debouncedFetchHandymen = useMemo(
    () =>
      debounce(() => {
        fetchHandymen();
      }, 300), // Reduced delay for better responsiveness
    [fetchHandymen],
  );

  useEffect(() => {
    // Fetch categories on mount
    props.getAllCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Debounced fetch when filters change
  useEffect(() => {
    debouncedFetchHandymen();
    return () => {
      debouncedFetchHandymen.cancel();
    };
  }, [
    selectedCategoryKeys,
    searchTerm,
    priceRange,
    hourlyRateRange,
    fixedPriceRange,
    onlyVerified,
    selectedAddress,
    selectedCoordinates,
    appliedRadiusKm,
    sort,
    debouncedFetchHandymen,
    isDesc,
  ]);

  // Categories are now loaded from Redux store via props

  // Intersection observer for lazy loading
  useEffect(() => {
    const currentRef = loadMoreRef.current;
    if (!currentRef || !showLoadMoreFooter) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          props.hasMore &&
          !props.isLoading &&
          !props.isLoadingMore
        ) {
          handleLoadMore();
        }
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1,
      },
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [
    props.hasMore,
    props.isLoading,
    props.isLoadingMore,
    handleLoadMore,
    showLoadMoreFooter,
  ]);

  const navigate = useNavigate();

  // Parse query parameters on page load (only if props are not provided)
  useEffect(() => {
    if (
      !props.preselectedCategoryKey &&
      (props.address === undefined || props.address === null) &&
      location.search
    ) {
      const queryParams = new URLSearchParams(location.search);

      // Get selected categories
      const categories = queryParams.get('selectedCategories');
      if (categories) {
        // Filter out empty strings and validate category keys
        const validCategoryKeys = categories
          .split(',')
          .map((key) => key.trim())
          .filter((key) => key !== '' && key.length > 0);

        if (validCategoryKeys.length > 0) {
          setSelectedCategoryKeys(new Set(validCategoryKeys));
        } else {
          setSelectedCategoryKeys(new Set());
        }
      }

      const address = queryParams.get('address');
      if (address) {
        setSelectedAddress(address);
        setAddressInputValue(address);
      }
    }
  }, []);

  // Store initial props values for reset functionality
  const initialAddressRef = React.useRef<string | null | undefined>(
    props.address,
  );
  const initialRadiusKmRef = React.useRef<number | undefined>(props.radiusKm);
  const initialCategoryKeyRef = React.useRef<string | undefined>(
    props.preselectedCategoryKey,
  );

  // Update refs when props change
  React.useEffect(() => {
    if (props.address !== undefined) {
      initialAddressRef.current = props.address;
    }
    if (props.radiusKm !== undefined) {
      initialRadiusKmRef.current = props.radiusKm;
    }
    if (props.preselectedCategoryKey !== undefined) {
      initialCategoryKeyRef.current = props.preselectedCategoryKey;
    }
  }, [props.address, props.radiusKm, props.preselectedCategoryKey]);

  // Sync props.address and props.radiusKm with internal state when props change
  useEffect(() => {
    if (props.address !== undefined && props.address !== null) {
      setSelectedAddress(props.address);
      setAddressInputValue(props.address); // Prefill the input field
      setSelectedCoordinates(null);
      setAppliedRadiusKm(props.radiusKm ?? 10);
    } else if (props.address === null) {
      // Explicitly null means clear the filter
      setSelectedAddress(null);
      setAddressInputValue('');
      setSelectedCoordinates(null);
      setAppliedRadiusKm(null);
    }
  }, [props.address, props.radiusKm]);

  // Handler to use order address
  const handleUseOrderAddress = useCallback(() => {
    if (props.address) {
      setAddressInputValue(props.address);
      setSelectedAddress(props.address);
      setSelectedCoordinates(null);
      setAppliedRadiusKm(props.radiusKm ?? 10);
    }
  }, [props.address, props.radiusKm]);

  // Sync preselectedCategoryKey with internal state when prop changes
  useEffect(() => {
    if (props.preselectedCategoryKey) {
      setSelectedCategoryKeys(new Set([props.preselectedCategoryKey]));
    } else if (
      props.preselectedCategoryKey === undefined &&
      !props.excludeFilters?.includes('categories')
    ) {
      // Only clear if categories filter is not excluded (i.e., it's a normal browse page)
      setSelectedCategoryKeys(new Set());
    }
  }, [props.preselectedCategoryKey, props.excludeFilters]);

  // On mount / whenever selected categories change, load or filter data
  useEffect(() => {
    if (!props.preselectedCategoryKey) {
      const selectedKeysArray = Array.from(selectedCategoryKeys).filter(
        (key) => key.trim() !== '',
      );
      const currentParams = new URLSearchParams(location.search);
      const currentCategories = currentParams.get('selectedCategories');

      if (selectedKeysArray.length > 0) {
        const joinedKeys = selectedKeysArray.join(',');
        // Only navigate if the URL categories are different from current state
        if (currentCategories !== joinedKeys) {
          navigate(
            `/browse-handyman?selectedCategories=${encodeURIComponent(
              joinedKeys,
            )}`,
          );
        }
      } else {
        // If no categories selected, navigate to clean URL only if there are currently categories in URL
        if (currentCategories) {
          navigate('/browse-handyman');
        }
      }
    }
  }, [selectedCategoryKeys, location.search]);

  /** Toggle a single category's checked state */
  const toggleCategory = useCallback((catKey: string) => {
    // Validate category key before processing
    if (!catKey || catKey.trim() === '') {
      console.warn('Invalid category key provided to toggleCategory:', catKey);
      return;
    }

    setSelectedCategoryKeys((prev) => {
      const updated = new Set(prev);
      // Remove any existing empty strings from the set
      updated.delete('');

      if (updated.has(catKey)) {
        updated.delete(catKey);
      } else {
        updated.add(catKey);
      }
      return updated;
    });
  }, []);

  const addAllSubcategoryKeys = (keys: string[]) => {
    setSelectedCategoryKeys((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => next.add(k));
      return next;
    });
  };

  const removeAllSubcategoryKeys = (keys: string[]) => {
    setSelectedCategoryKeys((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => next.delete(k));
      return next;
    });
  };

  /** "Select All" means check every category key. */
  // const handleSelectAllCategories = useCallback(() => {
  //   // Filter out any empty or invalid category keys
  //   const validCategoryKeys = ALL_CATEGORY_KEYS.filter(
  //     (key) => key && key.trim() !== '',
  //   );
  //   setSelectedCategoryKeys(new Set(validCategoryKeys));
  // }, [ALL_CATEGORY_KEYS]);

  // const handleDeselectAllCategories = useCallback(() => {
  //   setSelectedCategoryKeys(new Set());
  // }, []);

  /** "Reset Filters" clears everything but preserves order address/category if provided via props. */
  const handleResetFilters = useCallback(() => {
    // Reset to initial props values if provided, otherwise clear
    if (initialCategoryKeyRef.current) {
      setSelectedCategoryKeys(new Set([initialCategoryKeyRef.current]));
    } else {
      setSelectedCategoryKeys(new Set());
    }

    if (
      initialAddressRef.current !== undefined &&
      initialAddressRef.current !== null
    ) {
      setSelectedAddress(initialAddressRef.current);
      setAddressInputValue(initialAddressRef.current);
      setAppliedRadiusKm(initialRadiusKmRef.current ?? 10);
    } else {
      setSelectedAddress(null);
      setAddressInputValue('');
      setAppliedRadiusKm(null);
    }
    setSelectedCoordinates(null);

    setHandymanSearchText('');
    setSearchTerm('');
    setPriceRange([0, 10000]);
    setOnlyVerified(false);
    setHourlyRateRange([0, 1000]);
    setFixedPriceRange([0, 10000]);
  }, []);

  // Handlers for the other filters
  const handleHandymanSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setHandymanSearchText(event.target.value);
      setSearchTerm(event.target.value);
    },
    [],
  );

  const handleHourlyRateRangeChange = useCallback(
    (event: Event, newValue: number | number[]) => {
      setHourlyRateRange(newValue as [number, number]);
    },
    [],
  );

  const handleFixedPriceRangeChange = useCallback(
    (event: Event, newValue: number | number[]) => {
      setFixedPriceRange(newValue as [number, number]);
    },
    [],
  );

  // Map popup handlers
  const handleOpenMapPopup = useCallback(() => {
    setIsMapPopupOpen(true);
  }, []);

  const handleCloseMapPopup = useCallback(() => {
    setIsMapPopupOpen(false);
  }, []);

  const handleApplyMapFilters = useCallback(
    (
      address: string,
      radius: number,
      coordinates: { lat: number; lng: number } | null,
    ) => {
      setSelectedAddress(address || null);
      setSelectedCoordinates(coordinates);
      setRadiusKm(radius);
      setAppliedRadiusKm(radius);
    },
    [],
  );

  /**
   * Memoized handyman list mapping and filtering
   */
  const filteredHandymen = useMemo(() => {
    return mapViewModelToHandyman(
      props.handymanList ?? [],
      selectedCategoryKeys,
    );
  }, [props.handymanList, selectedCategoryKeys]);

  useEffect(() => {
    if (!props.hasMore && !props.isLoadingMore && filteredHandymen.length > 0) {
      const timer = setTimeout(() => {
        setShowLoadMoreFooter(false);
      }, 3000);

      return () => clearTimeout(timer);
    }

    setShowLoadMoreFooter(true);
  }, [props.hasMore, props.isLoadingMore, filteredHandymen.length]);

  const isCategoriesFilterVisible =
    !props.excludeFilters?.includes('categories');
  const isLocationFilterVisible = !props.excludeFilters?.includes('location');
  const isNameFilterVisible = !props.excludeFilters?.includes('name');
  // const isPriceRangeFilterVisible =
  //   !props.excludeFilters?.includes('price-range');

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileSortOpen, setMobileSortOpen] = useState(false);
  const filtersOpen = !isMobile || mobileFiltersOpen;
  const sortOpen = !isMobile || mobileSortOpen;

  const handleFiltersOpen = () => {
    setMobileFiltersOpen(!mobileFiltersOpen);
  };
  const handleSortOpen = () => {
    setMobileSortOpen(!mobileSortOpen);
  };
  useEffect(() => {
    if (!isMobile) setMobileFiltersOpen(false);
  }, [isMobile]);

  const hasActiveFilters = useMemo(() => {
    const hasCategories = selectedCategoryKeys.size > 0;

    const hasAddress = !!selectedAddress; // або addressInputValue
    const hasRadius = (appliedRadiusKm ?? null) !== null;

    const hasSearch = (searchTerm ?? '').trim().length > 0;

    const hasHourly = hourlyRateRange[0] > 0 || hourlyRateRange[1] < 1000;

    const hasFixed = fixedPriceRange[0] > 0 || fixedPriceRange[1] < 10000;

    const hasVerified = onlyVerified === true;

    return (
      hasCategories ||
      hasAddress ||
      hasRadius ||
      hasSearch ||
      hasHourly ||
      hasFixed ||
      hasVerified
    );
  }, [
    selectedCategoryKeys,
    selectedAddress,
    appliedRadiusKm,
    searchTerm,
    hourlyRateRange,
    fixedPriceRange,
    onlyVerified,
  ]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: '24px',
          width: '100%',
          maxWidth: '1132px',
          margin: '0 auto',
        }}
      >
        {/* Left sidebar for Category Tree & filters */}
        {needFilters && (
          <Box
            sx={{
              width: { xs: '100%', md: '358px' },
              flexShrink: 0,
              gap: '24px',
              py: { xs: '0', md: '24px' },
              position: { xs: 'static', md: 'sticky' },
              top: { md: 100 },
              alignSelf: { md: 'flex-start' },
              boxShadow: {
                xs: 'unset',
                md: '10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2), -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.05), 14.1509px 14.1509px 22.6415px -0.373585px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3), inset -2.0717px -2.63774px 4.52338px -1.50779px rgba(0, 0, 0, 0.3), inset 1.57925px 1.57925px 1.69811px #FFFFFF',
              },
              borderRadius: '32px',
              backdropFilter: 'blur(12)',
            }}
          >
            <Box
              sx={{
                maxHeight: { xs: 'none', md: 'calc(100vh - 168px)' },
                overflowY: { xs: 'unset', md: 'auto' },
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {/* toggle button on mobile for sort and filter blocks */}
              {isMobile && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '16px',
                    p: 0,
                  }}
                >
                  <NeumorphicButton
                    onClick={() => handleFiltersOpen()}
                    sx={{
                      height: '48px',
                      width: '192px',
                      minWidth: '120px',
                      fontSize: '16px',
                      '& svg': { mr: '8px' },
                    }}
                  >
                    <FiltersSvg />
                    Filters
                  </NeumorphicButton>
                  <NeumorphicButton
                    onClick={handleSortOpen}
                    sx={{
                      width: '192px',
                      height: '48px',
                      minWidth: '120px',
                      fontSize: '16px',
                      '& svg': { mr: '8px' },
                    }}
                  >
                    <SortSvg />
                    Sort
                  </NeumorphicButton>
                </Box>
              )}

              <BrowseHandymanFilters
                filtersOpen={filtersOpen}
                isMobile={isMobile}
                isDark={isDark}
                setMobileFiltersOpen={setMobileFiltersOpen}
                isCategoriesFilterVisible={isCategoriesFilterVisible}
                isLocationFilterVisible={isLocationFilterVisible}
                isNameFilterVisible={isNameFilterVisible}
                isFetchingCategories={props.isFetchingCategories}
                categoriesError={props.categoriesError}
                transformedCategories={transformedCategories}
                selectedCategoryKeys={selectedCategoryKeys}
                toggleCategory={toggleCategory}
                addAllSubcategoryKeys={addAllSubcategoryKeys}
                removeAllSubcategoryKeys={removeAllSubcategoryKeys}
                getAllCategories={props.getAllCategories}
                autocompleteRef={autocompleteRef}
                selectedAddress={selectedAddress}
                addressInputValue={addressInputValue}
                setAddressInputValue={setAddressInputValue}
                setSelectedAddress={setSelectedAddress}
                setSelectedCoordinates={setSelectedCoordinates}
                address={props.address}
                handleUseOrderAddress={handleUseOrderAddress}
                radiusKm={radiusKm}
                setRadiusKm={setRadiusKm}
                setAppliedRadiusKm={setAppliedRadiusKm}
                handleOpenMapPopup={handleOpenMapPopup}
                handymanSearchText={handymanSearchText}
                handleHandymanSearchChange={handleHandymanSearchChange}
                hourlyRateRange={hourlyRateRange}
                handleHourlyRateRangeChange={handleHourlyRateRangeChange}
                fixedPriceRange={fixedPriceRange}
                handleFixedPriceRangeChange={handleFixedPriceRangeChange}
                handleResetFilters={handleResetFilters}
                hasActiveFilters={hasActiveFilters}
              />
            </Box>
          </Box>
        )}
        {/* Main content area */}
        <Box
          sx={{
            width: { xs: '100%', md: '750px' },
            flex: 1,
            minWidth: 0,
          }}
        >
          <BrowseHandymanSort
            sortOpen={sortOpen}
            isMobile={isMobile}
            isDark={isDark}
            sort={sort as SortValue}
            setSort={setSort}
            setIsDesc={setIsDesc}
            setMobileSortOpen={setMobileSortOpen}
          />
          {props.isLoading ? (
            <Box>
              {[1, 2, 3, 4, 5, 6].map((index) => (
                <HandymanCard
                  key={index}
                  isSkeleton={true}
                  handyman={undefined}
                  onAction={undefined}
                  actionButtonTitle={undefined}
                  categoryDictionary={categoryDictionary}
                  categories={groupedCategories}
                />
              ))}
            </Box>
          ) : (
            <Box>
              {filteredHandymen.length > 0 ? (
                <>
                  {filteredHandymen.map((handyman) => {
                    return (
                      <HandymanCard
                        key={handyman.id}
                        handyman={handyman}
                        onAction={() =>
                          props.onAction(
                            {
                              id: handyman.id,
                              name: handyman.name,
                              categories: handyman.categories,
                              city: handyman.city,
                              cityDistrict: handyman.cityDistrict,
                            },
                            selectedCategoryKeys.size > 0
                              ? Array.from(selectedCategoryKeys)
                              : undefined,
                          )
                        }
                        isNotHandyman={props.isNotHandyman}
                        actionButtonTitle={
                          typeof props.actionButtonTitle === 'function'
                            ? props.actionButtonTitle(handyman.id)
                            : props.actionButtonTitle
                        }
                        categoryDictionary={categoryDictionary}
                        categories={groupedCategories}
                        lastReviews={(
                          userReviewsByUserId?.[handyman.id] ?? []
                        ).slice(0, 2)}
                        reviewsLoading={
                          !!userReviewsLoadingByUserId?.[handyman.id]
                        }
                        onNeedReviews={ensureReviewsLoaded}
                        isCategoriesVisible={!hasActiveFilters}
                        sentProposalIds={props.sentProposalIds}
                        // ensurePortfolioLoaded={ensurePortfolioLoaded}
                      />
                    );
                  })}

                  {/* Lazy loading trigger and indicator */}

                  <Collapse in={showLoadMoreFooter} timeout={400}>
                    <Fade in={showLoadMoreFooter} timeout={300}>
                      <Box
                        ref={loadMoreRef}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          py: 3,
                          minHeight: '80px',
                        }}
                      >
                        {props.isLoadingMore && (
                          <CircularProgress
                            size={32}
                            sx={{ color: isDark ? '#00E8E7' : '#005CAD' }}
                          />
                        )}
                        {!props.hasMore && filteredHandymen.length > 0 && (
                          <Typography
                            sx={{
                              fontFamily: 'Roboto',
                              fontSize: '14px',
                              color: isDark ? '#B6B6B6' : '#585858',
                            }}
                          >
                            No more handymen to load
                          </Typography>
                        )}
                      </Box>
                    </Fade>{' '}
                  </Collapse>
                </>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                    fontFamily: 'Roboto',
                    fontSize: '16px',
                    color: isDark ? '#D5D5D5' : '#585858',
                  }}
                >
                  No handymen found matching your filters
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Search on Map Popup */}
      <SearchOnMapPopup
        open={isMapPopupOpen}
        onClose={handleCloseMapPopup}
        onApplyFilters={handleApplyMapFilters}
        initialAddress={selectedAddress || ''}
        initialCoordinates={selectedCoordinates}
        initialRadius={radiusKm || 10}
        handymanCount={filteredHandymen.length}
        availableCount={filteredHandymen.filter((h) => h.isVerified).length}
        searchArea={selectedAddress || 'Current Location'}
        isDark={isDark}
      />
    </>
  );
};

export default connector(BrowseHandymanList);
